import {
  expireLegacySessionCookie,
  getLegacySessionCookie,
  getProfileId,
  getSessionCookie,
  updateSessionCookie,
} from "@prmf/cloud-utils/cookies.server";
import {
  AuthBar,
  CookieBar,
  FooterAccreditations,
  FooterNav,
  GA,
  Menu,
  PrintContext,
  ProfileContextData,
  ProfileProvider,
} from "@prmf/site-components";
import { routes, t } from "@prmf/site-router";
import type {
  HeadersFunction,
  LinksFunction,
  LoaderFunctionArgs,
} from "@remix-run/node";
import {
  Links,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunctionArgs,
  json,
  redirect,
  useLoaderData,
  useNavigation,
} from "@remix-run/react";
import {
  DocumentReference,
  getFirestore,
  Timestamp,
} from "firebase-admin/firestore";
import { useCallback, useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import "./index.css";

export const headers: HeadersFunction = () => ({
  //https://firebase.google.com/docs/hosting/manage-cache
  //"Cache-Control": "public, max-age=86400, s-max-age=86400",
});

export const meta: MetaFunction = ({ location }) => {
  const origin = import.meta.env.VITE_SITE_ORIGIN;
  //const url = new URL(location.pathname, origin);
  const path = location.pathname;
  const title = `${t(path, "title", false) || "Welcome"} – Paula Rosa Manhattan`;
  const description = t(path, "description", false);
  const image = new URL(t(path, "image", false) || "", origin);
  return [
    { title },
    { name: "description", content: description },
    { name: "og:title", content: title },
    { name: "og:description", content: description },
    { name: "og:image", content: image },
    { property: "name", content: title },
    { property: "description", content: description },
    { property: "image", content: image },
  ];
};

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@100;300;400;500;700&display=swap",
  },
  { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
  {
    rel: "icon",
    type: "image/png",
    sizes: "32x32",
    href: "/favicon-32x32.png",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "16x16",
    href: "/favicon-16x16.png",
  },
  { rel: "manifest", href: "/site.webmanifest" },
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  // Temporarily delete legacy cookie where wildcard domain wasn't defined
  if (await getLegacySessionCookie(request)) {
    const url = new URL(request.url);
    console.log("will remove legacy cookie");
    return redirect(url.pathname, {
      headers: { "Set-Cookie": await expireLegacySessionCookie() },
    });
  }

  const profileId = await getProfileId(request).catch(() => undefined);
  let customerType: string | undefined;
  let customerName: string | undefined;
  let profile: ProfileContextData = {
    auth: false,
    bookmarks: [],
    moodboards: [],
    messages: [],
  };

  if (profileId) {
    const profileRef = getFirestore().collection("profiles").doc(profileId);
    const doc = await profileRef.get();

    profile.auth = true;
    profile.name = doc.get("name");
    profile.sector = doc.get("sector");
    profile.logo = doc.get("logo");
    profile.cover = doc.get("cover");

    customerName = profile.name;
    customerType = profile.sector;

    const messages = (
      await profileRef.collection("messages").orderBy("createdAt", "desc").get()
    ).docs.map((d) => ({
      id: d.id,
      title: d.get("title"),
      text: d.get("text"),
      createdAt: (d.get("createdAt") as Timestamp | undefined)
        ?.toDate()
        .toJSON(),
      readAt: (d.get("readAt") as Timestamp | undefined)?.toDate().toJSON(),
    }));

    profile.messages = messages;

    const moodboards = doc.get("moodboards") as DocumentReference[] | undefined;
    if (moodboards) {
      profile.bookmarks = (
        await Promise.all(
          moodboards.map(async (ref) => {
            const board = await ref.get();
            if (!profile.moodboards.some((m) => m.id === ref.id)) {
              profile.moodboards.push({
                id: ref.id,
                title: board.get("title"),
                createdAt: board.get("createdAt")?.toMillis(),
              });
            }

            const items = board.get("items") as string[] | undefined;
            return items?.map((id) => ({ moodboardId: ref.id, id }));
          }),
        )
      )
        .flat()
        .filter((b) => b !== undefined);
    }
    profile.moodboards.sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1));
  }

  const url = new URL(request.url);
  const ctParam = url.searchParams.get("type");
  if (ctParam && ["private", "social", "bespoke", "retail"].includes(ctParam)) {
    const expires = new Date();
    expires.setUTCMonth(expires.getUTCMonth() + 1);
    if (ctParam === "retail") {
      return redirect(routes.retailCustomers, 301);
    }
    return redirect(url.pathname, {
      headers: {
        "Set-Cookie": await updateSessionCookie(request, {
          customerType: ctParam,
        }),
      },
    });
  }
  if (!customerType) {
    customerType = (await getSessionCookie(request))?.customerType;
  }
  if (!profile.sector && customerType) {
    profile.sector = customerType;
  }
  return json({
    profile,
    customerType,
    customerName,
  });
};

export function shouldRevalidate({
  defaultShouldRevalidate,
  formAction,
}: ShouldRevalidateFunctionArgs) {
  if (formAction === "/account/moodboard") return false;
  return defaultShouldRevalidate;
}

function useTracking() {
  const key = "_tracking";
  const [tracking, setTracking] = useState<number | undefined>(0);

  useEffect(() => {
    const ls = localStorage.getItem(key);
    const v = ls === null ? undefined : parseInt(ls);
    setTracking(v);
  }, []);

  const update = useCallback(
    (newValue?: number) => {
      if (newValue === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, newValue.toString());
      }
      setTracking(newValue);
    },
    [tracking],
  );

  return [tracking, update] as const;
}

export default function App() {
  const data = useLoaderData<typeof loader>();
  const { state } = useNavigation();
  const [tracking, setTracking] = useTracking();

  return (
    <html lang="en" className={data.customerType}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="flex min-h-screen flex-col">
        <GA active={Boolean(tracking)} />
        <Toaster containerClassName="text-sm" />
        <Tooltip id="tooltip" className="z-50" />
        {state !== "idle" && (
          <div className="bg-accent fixed inset-x-0 top-0 h-1 animate-pulse" />
        )}
        <PrintContext>
          <ProfileProvider value={data.profile}>
            <header className="print:hidden">
              <AuthBar />
              <Menu />
            </header>
            <main className="flex-1 print:flex-none">
              <Outlet />
            </main>
            <div className="print:hidden">
              <FooterAccreditations />
              <FooterNav />
            </div>
          </ProfileProvider>
        </PrintContext>
        <ScrollRestoration />
        <Scripts />
        <CookieBar
          show={tracking === undefined}
          onChange={setTracking}
          className="print:hidden"
        />
      </body>
    </html>
  );
}
